/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * MobilePhotosIntake.jsx — Safari-safe iPhone Photos picker bridge.
 *
 * P0 May 22, 2026 — Truth-by-files-presence adapter.
 *
 * The plain-HTML probe at /native-probe.html proved that on iOS
 * Safari, the Photos picker emits `cancel` (NOT `change`) for many
 * video / bare-mode selections — BUT `input.files` is populated
 * either way. Polling after `cancel` also shows `finalLen=1`.
 *
 * Conclusion: input.files is the source of truth, NOT the event
 * type. This adapter resolves picker close by inspecting
 * input.files, not by trusting which event fired.
 *
 * Rules baked in:
 *   1. change / input → snapshot files immediately, stage them.
 *   2. cancel → DO NOT abort. Inspect input.files anyway.
 *   3. After cancel / window-focus / pageshow / visibilitychange /
 *      poll end → re-inspect input.files. If .length > 0, stage.
 *   4. Only treat as real cancel if input.files.length === 0
 *      after a short settle window.
 *   5. Never clear input.value before files are synchronously
 *      copied into a plain Array.
 *   6. Hard dedup guard: stage every File only once per picker
 *      session, keyed on (name, size, lastModified, type). So if
 *      change and cancel and poll all carry the same FileList, the
 *      parent only sees them once.
 *
 * The public contract (forwardRef API) is unchanged so the existing
 * ClipBasketV2 consumer keeps working without modification.
 */
import { forwardRef, useImperativeHandle, useRef, useEffect, useCallback } from "react";
import { intakeLog } from "../../lib/intakeLog";
import {
  acceptPickerHandoff,
  newSessionFingerprintSet,
} from "../../lib/acceptPickerHandoff";
import { isIOSSafari } from "../../lib/iosDetect";

const SETTLE_WINDOW_MS = 12_000;   // after openPicker, listen this long
const POLL_INTERVAL_MS = 200;
const REAL_CANCEL_DELAY_MS = 1_500; // wait this long before declaring "no files arrived"
const WATCHDOG_MS = 60_000;

// fingerprintFile lives in /app/frontend/src/lib/acceptPickerHandoff.js
// — there is exactly ONE implementation of the contract in the
// codebase. This file imports and delegates.

const MobilePhotosIntake = forwardRef(function MobilePhotosIntake(
  {
    onFilesStaged,
    onPickerStuck,                    // optional — fires only if files truly never arrive
    accept = "video/*,.mp4,.mov,.m4v,.mkv",
    testid = "mobile-photos-intake",
  },
  ref,
) {
  const inputRef = useRef(null);
  const handlerRef = useRef(onFilesStaged);
  const stuckRef = useRef(onPickerStuck);
  handlerRef.current = onFilesStaged;
  stuckRef.current = onPickerStuck;

  // ── iPhone-safe input shape (May 22, 2026) ──────────────────────
  // The native-probe matrix proved that on iPhone Safari (and
  // desktop-UA Safari on touch hardware), the unstable picker shape
  // is `hidden + accept="video/*" + multiple` — the OS returns
  // `cancel` with files.length === 0 even when the user picked
  // clips. The probe-proven safe shape is `accept` OMITTED, with
  // `multiple` preserved. On non-iOS browsers the original
  // `accept` filter is kept so the picker UX stays tight.
  //
  // This is the entire fix for the operator-reported "no clips
  // staged from Photos on iPhone" regression. Nothing else in the
  // intake or upload chain has changed.
  const iosSafe = isIOSSafari();
  const effectiveAccept = iosSafe ? undefined : accept;
  const intakePath = iosSafe ? "ios_safe_no_accept_multi" : "desktop_accept_multi";

  // Per-session state. Reset at every openPicker() call.
  const stagedSetRef = useRef(newSessionFingerprintSet());
  const sessionActiveRef = useRef(false);
  const sessionStartRef = useRef(0);
  const pollTimerRef = useRef(null);
  const settleTimeoutRef = useRef(null);
  const watchdogTimeoutRef = useRef(null);
  const stagedSomethingRef = useRef(false);

  // Listeners registered ONLY during a settle window so unrelated
  // focus / pageshow events outside a picker session don't poll the
  // input and stage stale entries.
  const settleListenersRef = useRef(null);

  // ── Core: THE picker handoff contract.
  //
  // Delegates to acceptPickerHandoff (the single source of truth
  // for "did a successful handoff happen"). Event type is irrelevant.
  // Files present in input.files = accepted handoff.
  const tryStageFromInput = useCallback((reason) => {
    const result = acceptPickerHandoff(
      inputRef.current,
      stagedSetRef.current,
    );
    if (result.total === 0) {
      intakeLog("intake_stage_skipped_empty", { reason });
      return false;
    }
    intakeLog("intake_stage_attempt", {
      reason,
      total_in_list: result.total,
      new_to_stage: result.newCount,
      already_staged: result.alreadyStagedCount,
    });
    if (result.newCount === 0) return false;
    try {
      handlerRef.current?.(result.files);
      stagedSomethingRef.current = true;
      intakeLog("intake_staged_inserted", { reason, count: result.newCount });
    } catch (err) {
      intakeLog("intake_stage_failed", {
        reason, error: String(err?.message || err),
      });
    }
    return true;
  }, []);

  // ── Settle window management ────────────────────────────────────
  const teardownSettle = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
    if (settleListenersRef.current) {
      const { onFocus, onPageshow, onVisibility } = settleListenersRef.current;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageshow);
      document.removeEventListener("visibilitychange", onVisibility);
      settleListenersRef.current = null;
    }
    sessionActiveRef.current = false;
  }, []);

  const armSettle = useCallback(() => {
    // Already armed? Don't double-arm.
    if (settleListenersRef.current) return;
    const onFocus = () => {
      intakeLog("intake_settle_window_focus");
      tryStageFromInput("settle_focus");
    };
    const onPageshow = (e) => {
      intakeLog("intake_settle_pageshow", { persisted: !!e?.persisted });
      tryStageFromInput("settle_pageshow");
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        intakeLog("intake_settle_visible");
        tryStageFromInput("settle_visibility");
      }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageshow);
    document.addEventListener("visibilitychange", onVisibility);
    settleListenersRef.current = { onFocus, onPageshow, onVisibility };

    // Polling — input.files may populate without ANY event firing
    // (the iOS bug signature we hunted in the probe).
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    let lastLen = inputRef.current?.files?.length || 0;
    pollTimerRef.current = setInterval(() => {
      const len = inputRef.current?.files?.length || 0;
      if (len !== lastLen) {
        intakeLog("intake_poll_files_changed", {
          prev: lastLen, next: len,
          ms_since_session_start: Date.now() - sessionStartRef.current,
        });
        lastLen = len;
        if (len > 0) tryStageFromInput("poll_files_changed");
      }
    }, POLL_INTERVAL_MS);

    // Auto-teardown after the settle window expires. ALSO decide
    // whether to fire onPickerStuck — only if we never staged.
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(() => {
      const finalLen = inputRef.current?.files?.length || 0;
      // Final check before tearing down.
      if (finalLen > 0) tryStageFromInput("settle_window_end");
      intakeLog("intake_settle_ended", {
        final_files_len: finalLen,
        staged_anything: stagedSomethingRef.current,
        duration_ms: Date.now() - sessionStartRef.current,
      });
      teardownSettle();
    }, SETTLE_WINDOW_MS);
  }, [teardownSettle, tryStageFromInput]);

  // ── Lifecycle ───────────────────────────────────────────────────
  useEffect(() => {
    intakeLog("intake_mounted");
    return () => {
      intakeLog("intake_unmounted");
      teardownSettle();
      if (watchdogTimeoutRef.current) {
        clearTimeout(watchdogTimeoutRef.current);
        watchdogTimeoutRef.current = null;
      }
    };
  }, [teardownSettle]);

  useImperativeHandle(
    ref,
    () => ({
      openPicker: () => {
        const node = inputRef.current;
        if (!node) {
          intakeLog("intake_open_no_node");
          return;
        }
        // Reset per-session dedup state via the shared contract.
        stagedSetRef.current = newSessionFingerprintSet();
        stagedSomethingRef.current = false;
        sessionActiveRef.current = true;
        sessionStartRef.current = Date.now();

        // .click() FIRST — preserve the iOS user-gesture chain.
        let clickErr = null;
        try { node.click(); } catch (err) { clickErr = String(err?.message || err); }
        intakeLog(clickErr ? "intake_open_click_failed" : "intake_open_clicked",
                  clickErr ? { error: clickErr } : {
                    // Operator-required diagnostic surface (May 22, 2026):
                    // every picker open MUST log which input shape we
                    // chose, so the lifecycle drawer / audit trail can
                    // prove which path was active on a given device.
                    path: intakePath,
                    accept: effectiveAccept || "(omitted)",
                    multiple: true,
                    ios_safe: iosSafe,
                    ua: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 120) : "",
                    touch_points: typeof navigator !== "undefined" ? (navigator.maxTouchPoints || 0) : null,
                  });

        // Arm settle listeners + polling for SETTLE_WINDOW_MS.
        armSettle();

        // Long-tail watchdog: if no files ever arrived after 60s,
        // surface the "we didn't receive your videos" callout.
        if (watchdogTimeoutRef.current) clearTimeout(watchdogTimeoutRef.current);
        watchdogTimeoutRef.current = setTimeout(() => {
          watchdogTimeoutRef.current = null;
          if (!stagedSomethingRef.current) {
            intakeLog("intake_watchdog_fired", { after_ms: WATCHDOG_MS });
            try { stuckRef.current?.(); } catch (_) { /* noop */ }
          } else {
            intakeLog("intake_watchdog_skipped_already_staged");
          }
        }, WATCHDOG_MS);
      },
    }),
    [armSettle],
  );

  // ── Input event handlers ────────────────────────────────────────
  const handleChange = (e) => {
    // Truth-by-files-presence: try to stage. The event type doesn't
    // matter; we just look at the FileList.
    intakeLog("intake_event_change", {
      from_event_len: e?.target?.files?.length ?? null,
      from_ref_len: inputRef.current?.files?.length ?? null,
    });
    tryStageFromInput("change");
  };

  const handleInput = (e) => {
    // Some iOS versions fire `input` before / instead of `change`.
    intakeLog("intake_event_input", {
      from_event_len: e?.target?.files?.length ?? null,
    });
    tryStageFromInput("input_event");
  };

  // CRITICAL: iOS Safari often emits `cancel` instead of `change`
  // for video/bare picks, but input.files IS populated. Treat
  // cancel as a hint to look at the FileList — not as a failure.
  const handleCancel = () => {
    intakeLog("intake_event_cancel", {
      files_len: inputRef.current?.files?.length ?? null,
    });
    // First check: maybe files arrived already.
    if (tryStageFromInput("cancel_immediate")) return;
    // No files yet. Wait a short settle window — iOS sometimes
    // populates the FileList AFTER dispatching cancel.
    setTimeout(() => {
      const finalLen = inputRef.current?.files?.length || 0;
      if (finalLen > 0) {
        tryStageFromInput("cancel_settle");
      } else {
        intakeLog("intake_cancel_confirmed_empty");
      }
    }, REAL_CANCEL_DELAY_MS);
  };

  return (
    <input
      ref={inputRef}
      type="file"
      multiple
      // iPhone-safe path (May 22, 2026): when isIOSSafari() is true
      // we OMIT the accept attribute entirely. The native-probe
      // matrix proved hidden+accept="video/*"+multiple is the
      // unstable shape on iOS WebKit.
      {...(effectiveAccept ? { accept: effectiveAccept } : {})}
      onChange={handleChange}
      onInput={handleInput}
      onCancel={handleCancel}
      data-testid={testid}
      data-intake-path={intakePath}
      data-accept-value={effectiveAccept || "(omitted)"}
      data-multiple="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
});

export default MobilePhotosIntake;
